import { useLogConversionEvent } from '@bfa/capi';
import {
  ConversionEventName,
  ConversionPageCategory,
} from '@bfa/capi/dist/constants';
import { clientLogging, cookieService } from '@bfa/nextjs-common/services';
import { externalRedirect } from '@bfa/nextjs-common/utils';
import { NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import URI from 'urijs';

import { ABSOLUTE_URLS, SUBSCRIPTION_FLOWS } from '../../../constants';
import { GQLProgram } from '../../../gql.generated';
import { logAnalyticsEvent } from '../../../utils/analytics';
import { AnalyticsEventKey } from '../../../utils/analytics/constants';
import { updateCookie } from '../../../utils/cookies';
import CookieName, {
  TTL_24_HOURS_IN_SECONDS,
} from '../../../utils/cookies/cookieName';
import { getGtmEvents, GtmEventsScriptProps } from '../../../utils/gtmEvents';
import { GBX_ANALYTICS_LABELS } from '../constants';
import useUpgradeGbx from '../graphql/hooks/useAddGbxSub';
import {
  extractGbxUpgradeData,
  getCurrentFlow,
  getCurrentFlowForAnalytics,
  getSubConfirmationSuccessCookie,
  shouldRedirectOnPaymentDecline,
  updateCookieOnAddonsOrUP,
} from '../utils';

export const analyticsKey: AnalyticsEventKey = 'GBX_ONE_CLICK_UPGRADE';

export const updateSubConfirmationCookies = (
  successPrograms: string,
  unsucessfulPrograms?: string,
  gift?: boolean,
  flow?: SUBSCRIPTION_FLOWS
) => {
  updateCookie(
    true,
    null,
    CookieName.SUB_CONFIRMATION_SUCCESS_PROGRAM,
    successPrograms,
    { maxAge: TTL_24_HOURS_IN_SECONDS }
  );
  updateCookie(true, null, CookieName.SUB_CONFIRMATION_GIFT, gift ?? false, {
    maxAge: TTL_24_HOURS_IN_SECONDS,
  });
  updateCookie(true, null, CookieName.SUB_CONFIRMATION_FLOW, flow, {
    maxAge: TTL_24_HOURS_IN_SECONDS,
  });
  updateCookie(
    true,
    null,
    CookieName.SUB_CONFIRMATION_UNSUCCESS_PROGRAM,
    unsucessfulPrograms,
    { maxAge: TTL_24_HOURS_IN_SECONDS }
  );
};

const redirectBasedOnFlow = (
  confirmationPageUrl: string,
  addonsUrl: string,
  upUrl: string,
  fromCheckout: boolean,
  router: NextRouter,
  upsaleFlow?: string
) => {
  if (upsaleFlow === SUBSCRIPTION_FLOWS.ADDONS) {
    router.push(addonsUrl);
  } else if (upsaleFlow === SUBSCRIPTION_FLOWS.UP) {
    router.push(upUrl);
  } else {
    const {
      subscriptionSuccessfullyCharged,
      paymentType,
      subscriptionCadence,
      subscriptionProgram,
    } = router.query;
    const membershipConfirmationUrl = new URI(confirmationPageUrl).addQuery({
      subscriptionSuccessfullyCharged,
      paymentType,
      subscriptionCadence,
      subscriptionProgram,
    });

    window.location.assign(
      fromCheckout ? membershipConfirmationUrl.href() : ABSOLUTE_URLS.GLAM_BAG
    );
  }
};

const useUpgradeToGBX = (
  fromCheckout: boolean,
  noCheckout: boolean,
  paymentRetryEnabled: boolean,
  isNotEligible: boolean,
  program?: GQLProgram,
  gtmEventsProps?: GtmEventsScriptProps,
  fireSubscriptionPaymentSuccess?: boolean
) => {
  const router = useRouter();
  const { flow } = router.query;
  const initialRender = useRef(true);
  const [onUpgradeError, setOnUpgradeError] = useState(false);
  const [onUpgradeloading, setOnUpgradeloading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [upgradeGbx, { loading: useUpgradeLoading }] = useUpgradeGbx();

  const confirmationPageUrl = `${ABSOLUTE_URLS.MEMBERSHIP_CONFIRMATION}?pixelsFired=true&noCheckout=${noCheckout}`;

  const addonsUrl = `/flash/addons/${program}/thanks?noCheckout=${noCheckout}`;

  const upUrl = `/ultra-personalization/thanks/${program}?noCheckout=${noCheckout}`;

  const paymentRetryUrl = `${ABSOLUTE_URLS.PAYMENT_RETRY}?program=GBX&redirectUrl=${confirmationPageUrl}`;

  const upsaleFlow = cookieService.getCookie(null, CookieName.SUB_UPSALE_FLOW);
  const onExitRedirect = () =>
    redirectBasedOnFlow(
      confirmationPageUrl,
      addonsUrl,
      upUrl,
      fromCheckout,
      router,
      upsaleFlow
    );

  const logAnalytics = useCallback(
    (label: string) => {
      const currentFlow = getCurrentFlowForAnalytics(flow, upsaleFlow);
      logAnalyticsEvent(
        {
          name: analyticsKey,
          label: `One Click_${label}`,
          properties: {
            fromCheckout,
            flow: currentFlow,
          },
        },
        null,
        false
      );
    },
    [fromCheckout, flow, upsaleFlow]
  );

  const toggleModal = (): void => {
    logAnalytics(
      showModal
        ? GBX_ANALYTICS_LABELS.CLOSED_LEARN_MORE
        : GBX_ANALYTICS_LABELS.CLICKED_LEARN_MORE
    );
    setShowModal((show) => !show);
  };

  const onCrossClick = async (
    event?: React.MouseEvent<Element, MouseEvent>
  ): Promise<void> => {
    event?.preventDefault();
    logAnalytics(GBX_ANALYTICS_LABELS.CLICKED_CROSS);
    onExitRedirect();
  };

  const onLogoClick = async (
    event?: React.MouseEvent<Element, MouseEvent>
  ): Promise<void> => {
    event?.preventDefault();
    externalRedirect(ABSOLUTE_URLS.GLAM_BAG);
  };

  const handleNoThanks = async (
    event?: React.MouseEvent<Element, MouseEvent>
  ): Promise<void> => {
    event?.preventDefault();
    logAnalytics(GBX_ANALYTICS_LABELS.NO_THANKS);
    onExitRedirect();
  };

  useEffect(() => {
    if (initialRender.current) {
      logAnalytics(GBX_ANALYTICS_LABELS.VIEWED_LANDING_PAGE);
      initialRender.current = false;
      if (isNotEligible) {
        router.push(ABSOLUTE_URLS.GLAM_BAG);
      }
    }
  }, [logAnalytics]);

  /**
   * Define function to handle the upgrade to gbx in one click.
   */
  async function handleUpgradeToGbx() {
    logAnalytics(GBX_ANALYTICS_LABELS.CLICKED_CONFIRMED);
    try {
      setOnUpgradeloading(true);
      const { data } = await upgradeGbx();

      const {
        isPaymentDecline,
        isGiftIncluded,
        errorTryingToUpgrade,
        errorMessage,
      } = extractGbxUpgradeData(data);

      setOnUpgradeError(errorTryingToUpgrade);
      if (errorTryingToUpgrade) {
        throw new Error(errorMessage);
      }
      const subConfirmationSuccessCookie =
        getSubConfirmationSuccessCookie(fromCheckout);
      const currentFlow = getCurrentFlow(fromCheckout, flow, upsaleFlow);
      updateCookieOnAddonsOrUP(currentFlow, program);
      updateSubConfirmationCookies(
        subConfirmationSuccessCookie,
        undefined,
        isGiftIncluded,
        currentFlow
      );
      logAnalytics(GBX_ANALYTICS_LABELS.SUCCESS);
      if (
        shouldRedirectOnPaymentDecline(paymentRetryEnabled, isPaymentDecline)
      ) {
        router.push(paymentRetryUrl);
      } else {
        window.location.replace(confirmationPageUrl);
      }
    } catch (mutationError) {
      logAnalytics(GBX_ANALYTICS_LABELS.FAILED);
      clientLogging.logError(mutationError as Error);
      setOnUpgradeError(true);
      setOnUpgradeloading(false);
    }
  }

  const subData = getGtmEvents(
    gtmEventsProps?.gbSubscription,
    gtmEventsProps?.gbpSubscription,
    gtmEventsProps?.gbxSubscription
  );

  useLogConversionEvent({
    event: ConversionEventName.PAYMENT_SUCCESS,
    pageCategory: ConversionPageCategory.SUBSCRIBE,
    shouldFire: !!fireSubscriptionPaymentSuccess,
    orderId: subData?.SUBSCRIBED?.id,
    value: subData?.SUBSCRIBED?.cost?.amount ?? '',
    currency: subData?.SUBSCRIBED?.cost?.currency ?? '',
  });

  return {
    handleUpgradeToGbx,
    handleNoThanks,
    onCrossClick,
    onLogoClick,
    logAnalytics,
    toggleModal,
    showModal,
    loading: onUpgradeloading || useUpgradeLoading || isNotEligible,
    onUpgradeError,
  };
};

export default useUpgradeToGBX;
